import React, { useEffect, useRef, useState } from 'react';
import { loadPlaces, parsePlace, placesConfigured, placesAuthFailed, onPlacesAuthFailure } from '../../../../../utils/googlePlaces';

/**
 * <AddressAutocomplete /> — an address input backed by Google Places.
 *
 * Pick a suggestion and the caller gets the tidied address, postcode, town and
 * coordinates in one go, so a new building is on the map immediately rather than
 * waiting for a geocode backfill. Typing a free-text address still works — this
 * degrades to an ordinary input if the key is missing or the script won't load.
 *
 * Props:
 *   value        current address text
 *   onChange     (text) => void            — every keystroke
 *   onSelect     (parsed) => void          — a suggestion was chosen
 *   country      ISO code(s) to bias to (default 'gb')
 */
const AddressAutocomplete = ({
  value, onChange, onSelect, placeholder = 'Start typing an address…',
  country = 'gb', autoFocus = false, style, id, ...aria
}) => {
  const inputRef = useRef(null);
  const acRef = useRef(null);
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;
  // Callers pass inline handlers; keep them in refs so the Places widget is built once.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const hintId = id ? `${id}-lookup-hint` : undefined;
  const [status, setStatus] = useState(placesConfigured() ? 'loading' : 'off');

  // Google refuses predictions asynchronously on a bad/unbilled key — reflect that.
  useEffect(() => onPlacesAuthFailure(() => setStatus('denied')), []);

  useEffect(() => {
    let cancelled = false;
    if (!placesConfigured()) return undefined;

    loadPlaces()
      .then((places) => {
        if (cancelled || !inputRef.current || acRef.current) return;
        const ac = new places.Autocomplete(inputRef.current, {
          fields: ['formatted_address', 'address_components', 'geometry', 'name'],
          ...(country ? { componentRestrictions: { country } } : {}),
        });
        ac.addListener('place_changed', () => {
          const parsed = parsePlace(ac.getPlace() || {});
          if (parsed.address) onChangeRef.current?.(parsed.address);
          onSelectRef.current?.(parsed);
        });
        acRef.current = ac;
        setStatus(placesAuthFailed() ? 'denied' : 'ready');
      })
      .catch(() => { if (!cancelled) setStatus('off'); });

    return () => {
      cancelled = true;
      // Google appends its dropdown to <body>; drop our listeners with the input.
      if (acRef.current && window.google?.maps?.event) {
        window.google.maps.event.clearInstanceListeners(acRef.current);
      }
      acRef.current = null;
    };
  }, [country]);

  return (
    <div>
      <div style={{ position: 'relative' }}>
        <input
          ref={inputRef}
          id={id}
          className="cw-input"
          {...Object.fromEntries(Object.entries(aria).filter(([k]) => k.startsWith('aria-')))}
          aria-describedby={[aria['aria-describedby'], (status === 'off' || status === 'denied') ? hintId : null].filter(Boolean).join(' ') || undefined}
          style={{ paddingLeft: 30, ...style }}
          value={value || ''}
          placeholder={status === 'ready' || status === 'loading' ? placeholder : 'e.g. 12 River Street, London'}
          autoFocus={autoFocus}
          autoComplete="off"
          // Enter would otherwise submit the modal while the suggestion list is open.
          onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
          onChange={(e) => onChange?.(e.target.value)}
        />
        <i className={`fa-solid ${status === 'loading' ? 'fa-spinner fa-spin' : 'fa-location-dot'}`} aria-hidden="true"
          style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--cw-placeholder)', pointerEvents: 'none' }} />
      </div>
      {(status === 'off' || status === 'denied') && (
        <div id={hintId} style={{ fontSize: 'var(--cs-text-xs)', color: status === 'denied' ? 'var(--cw-warning-fg)' : 'var(--cw-text-muted)', marginTop: 4, display: 'flex', alignItems: 'center', gap: 5 }}>
          <i className="fa-solid fa-circle-info" aria-hidden="true" />
          {status === 'denied'
            ? 'Address lookup isn’t available right now — type the address manually.'
            : 'Address lookup unavailable — type the address manually.'}
        </div>
      )}
    </div>
  );
};

export default AddressAutocomplete;
